import { Combobox, SearchField, SegmentedControl } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";

import type { CatalogSortOrder } from "@/music-catalog/utils/catalog-view";

export type CatalogFilter = "all" | "duplicates";
export type CatalogView = "covers" | "tracks";

/**
 * Narrowing the catalog: what to search for, what to show, in what order, and
 * as what.
 *
 * The desktop offers a "Missing" filter as well, for a project indexed there
 * whose file has gone from the machine. It has no meaning in the cloud — a
 * project here is its stored bytes — and a filter that can only ever answer
 * zero is a control that teaches the user it does nothing.
 */
export const CatalogToolbar = ({
  duplicateProjectCount,
  filter,
  onFilterChange,
  onOrderChange,
  onQueryChange,
  onViewChange,
  order,
  projectCount,
  query,
  view,
}: {
  duplicateProjectCount: number;
  filter: CatalogFilter;
  onFilterChange: (filter: CatalogFilter) => void;
  onOrderChange: (order: CatalogSortOrder) => void;
  onQueryChange: (query: string) => void;
  onViewChange: (view: CatalogView) => void;
  order: CatalogSortOrder;
  projectCount: number;
  query: string;
  view: CatalogView;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-56 flex-1">
        <SearchField
          value={query}
          onValueChange={onQueryChange}
          label={t("catalog.search")}
          placeholder={t("catalog.searchPlaceholder")}
        />
      </div>

      <SegmentedControl
        label={t("catalog.filter")}
        value={filter}
        onValueChange={(value) => onFilterChange(value as CatalogFilter)}
        options={[
          { count: projectCount, label: t("catalog.filterAll"), value: "all" },
          { count: duplicateProjectCount, label: t("catalog.duplicates"), value: "duplicates" },
        ]}
      />

      <Combobox
        label={t("catalog.sort")}
        isLabelHidden
        value={order}
        onChange={(value) => onOrderChange(value as CatalogSortOrder)}
        options={[
          { label: t("catalog.sortUpdated"), value: "updated" },
          { label: t("catalog.sortName"), value: "name" },
          { label: t("catalog.sortTracks"), value: "tracks" },
          { label: t("catalog.sortDuration"), value: "duration" },
        ]}
      />

      <SegmentedControl
        label={t("catalog.view")}
        value={view}
        onValueChange={(value) => onViewChange(value as CatalogView)}
        options={[
          { label: t("catalog.viewCovers"), value: "covers" },
          { label: t("catalog.viewTracks"), value: "tracks" },
        ]}
      />
    </div>
  );
};
